import { BusinessStatusBanner } from "@nwga/features/businesses/ui";
import { useActor, useTRPC } from "@nwga/features/client";
import { Button } from "@nwga/ui/components/button";
import { Skeleton } from "@nwga/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	Outlet,
	redirect,
} from "@tanstack/react-router";
import { ExternalLinkIcon } from "lucide-react";
import { BusinessSwitcher } from "../../../components/business-switcher.tsx";
import {
	ConsoleShell,
	SidebarNav,
	SidebarNavLink,
} from "../../../components/console-shell.tsx";
import { Container } from "../../../components/container.tsx";
import { ErrorState } from "../../../components/error-state.tsx";
import { workspaceSectionsFor } from "../../../navigation.ts";

/**
 * One business's workspace.
 *
 * Everything you can do *to* a business hangs off this layout, so the chrome —
 * which business, what state it is in, what else you could be doing — is
 * rendered once and every screen underneath inherits it. Sections used to be
 * tabs inside a single page, which meant "your team" had no URL of its own and
 * a reload always dumped you back on the listings.
 *
 * Membership is checked here rather than on each child. A signed-in visitor who
 * is not a member of this business is sent back to their own dashboard: the
 * server refuses them regardless, and a shell full of empty panels and failed
 * requests is a worse way to find that out.
 */
export const Route = createFileRoute("/dashboard/$businessSlug")({
	beforeLoad: async ({ context, params }) => {
		const [business, session] = await Promise.all([
			context.queryClient.ensureQueryData(
				context.trpc.businesses.bySlug.queryOptions({
					slug: params.businessSlug,
				}),
			),
			context.queryClient.ensureQueryData(context.trpc.session.queryOptions()),
		]);

		const member = session.memberships.some(
			(membership) => membership.businessId === business.id,
		);

		if (!member) throw redirect({ to: "/dashboard", replace: true });

		return { business };
	},

	errorComponent: ({ error, reset }) => (
		<Container size="narrow" className="py-12">
			<ErrorState error={error} onRetry={reset} />
		</Container>
	),

	component: BusinessWorkspaceLayout,
});

function BusinessWorkspaceLayout() {
	const { businessSlug } = Route.useParams();
	const trpc = useTRPC();
	const actor = useActor();

	const business = useQuery(
		trpc.businesses.bySlug.queryOptions({ slug: businessSlug }),
	);

	if (business.isPending) {
		return (
			<ConsoleShell sidebar={<Skeleton className="h-56 w-full" />}>
				<Skeleton className="h-64 w-full" />
			</ConsoleShell>
		);
	}

	if (business.isError) {
		return (
			<Container size="narrow" className="py-12">
				<ErrorState
					error={business.error}
					onRetry={() => void business.refetch()}
				/>
			</Container>
		);
	}

	// The same rules the server enforces, so a section a member cannot use is
	// not offered to them. `permissions.ts` in `@nwga/auth` is the one copy.
	const sections = workspaceSectionsFor(actor, business.data.id);

	return (
		<ConsoleShell
			banner={<BusinessStatusBanner business={business.data} />}
			sidebar={
				<div className="space-y-6">
					<BusinessSwitcher currentSlug={businessSlug} />

					<SidebarNav label="Manage">
						{sections.map((section) => (
							<SidebarNavLink
								key={section.to}
								to={section.to}
								params={{ businessSlug }}
								label={section.label}
								icon={section.icon}
								exact={section.exact}
							/>
						))}
					</SidebarNav>

					{/* Only once it is on the board: before approval the public page is a
              404, and offering a link to one is worse than offering none. */}
					{business.data.status === "active" && (
						<Button
							asChild
							variant="ghost"
							size="sm"
							className="w-full justify-start"
						>
							<Link to="/businesses/$slug" params={{ slug: businessSlug }}>
								<ExternalLinkIcon className="size-4" aria-hidden="true" />
								View public page
							</Link>
						</Button>
					)}
				</div>
			}
		>
			<Outlet />
		</ConsoleShell>
	);
}
